import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Alert } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";

const ENTITY_TYPE_OPTIONS = [
  { value: "PRESCRIBER", label: "Prescriber" },
  { value: "MBR", label: "Member" },
  { value: "PHARM", label: "Pharmacy" },
];

const ASSIGNMENTS_PER_RUN_MIN = 1;
const HHMM_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const BUSINESS_DAY_START_MIN = "06:00"; // 6 AM
const BUSINESS_DAY_END_MAX = "18:00";   // 6 PM
const SCHEDULER_INTERVAL_MINUTES = 5;

function parseHHmm(hhmm) {
  if (!hhmm || !HHMM_REGEX.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function getEstimatedPerDay(startCst, endCst, value) {
  const run = Number(value);
  if (value == null || value === "" || !Number.isInteger(run) || run < 1) return null;
  const start = parseHHmm(startCst);
  const end = parseHHmm(endCst);
  if (start == null || end == null) return null;
  const minutesInWindow = end - start;
  if (minutesInWindow <= 0) return null;
  const runsPerDay = Math.floor(minutesInWindow / SCHEDULER_INTERVAL_MINUTES);
  if (runsPerDay === 0) return null;
  return runsPerDay * run;
}

function hasValidTimeOrder(startCst, endCst) {
  const start = parseHHmm(startCst);
  const end = parseHHmm(endCst);
  if (start == null || end == null) return false;
  return end > start;
}

function isWindowTooSmallForScheduler(startCst, endCst) {
  const start = parseHHmm(startCst);
  const end = parseHHmm(endCst);
  if (start == null || end == null) return false;
  const minutesInWindow = end - start;
  if (minutesInWindow <= 0) return false;
  return minutesInWindow < SCHEDULER_INTERVAL_MINUTES;
}

function validateForm(config) {
  const errors = [];
  const maxBatchSize = config.maxBatchSize != null ? Number(config.maxBatchSize) : null;

  if (config.assignmentsPerRunCall == null || config.assignmentsPerRunCall === "") {
    errors.push("Assignments per run (call) is required.");
  } else {
    const n = Number(config.assignmentsPerRunCall);
    if (!Number.isInteger(n) || n < ASSIGNMENTS_PER_RUN_MIN) {
      errors.push("Assignments per run (call) must be at least 1.");
    } else if (maxBatchSize != null && n > maxBatchSize) {
      errors.push(`Assignments per run (call) must not exceed ${maxBatchSize}.`);
    }
  }
  if (config.assignmentsPerRunTask == null || config.assignmentsPerRunTask === "") {
    errors.push("Assignments per run (task) is required.");
  } else {
    const n = Number(config.assignmentsPerRunTask);
    if (!Number.isInteger(n) || n < ASSIGNMENTS_PER_RUN_MIN) {
      errors.push("Assignments per run (task) must be at least 1.");
    } else if (maxBatchSize != null && n > maxBatchSize) {
      errors.push(`Assignments per run (task) must not exceed ${maxBatchSize}.`);
    }
  }
  if (config.businessDayStartCst && !HHMM_REGEX.test(config.businessDayStartCst)) {
    errors.push("Business day start must be in HH:mm format (e.g. 08:00).");
  } else if (config.businessDayStartCst) {
    const startMins = parseHHmm(config.businessDayStartCst);
    if (startMins != null && startMins < parseHHmm(BUSINESS_DAY_START_MIN)) {
      errors.push("Business day start must be 6:00 AM or later.");
    }
  }
  if (config.businessDayEndCst && !HHMM_REGEX.test(config.businessDayEndCst)) {
    errors.push("Business day end must be in HH:mm format (e.g. 17:00).");
  } else if (config.businessDayEndCst) {
    const endMins = parseHHmm(config.businessDayEndCst);
    if (endMins != null && endMins > parseHHmm(BUSINESS_DAY_END_MAX)) {
      errors.push("Business day end must be 6:00 PM or earlier.");
    }
  }
  if (
    config.businessDayStartCst &&
    config.businessDayEndCst &&
    HHMM_REGEX.test(config.businessDayStartCst) &&
    HHMM_REGEX.test(config.businessDayEndCst)
  ) {
    const startMins = parseHHmm(config.businessDayStartCst);
    const endMins = parseHHmm(config.businessDayEndCst);
    if (startMins != null && endMins != null && endMins <= startMins) {
      errors.push("Business day end must be after business day start.");
    }
  }
  return errors;
}

const emptyFormState = () => ({
  enabled: false,
  assignmentsPerRunCall: null,
  assignmentsPerRunTask: null,
  businessDayStartCst: "",
  businessDayEndCst: "",
  allowedEntityTypes: [],
});

const AiAgentConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const fetchConfig = useCallback(() => {
    setLoading(true);
    setError(null);
    sendAuthenticatedAsyncRequest(
      "/agent-workspace/ai-agent/ai-assigner-setup?includeBatchSizeLimits=true",
      "GET",
      null,
      (response) => {
        const data = response?.data;
        setConfig(data != null ? { ...data } : emptyFormState());
        setLoading(false);
      },
      (err) => {
        const status = err?.response?.status;
        if (status === 404) {
          setConfig(emptyFormState());
        } else {
          setError("Could not load config.");
          showNotification({
            title: "Error",
            message: getErrorMessage(err?.response?.data) || "Could not load config.",
            position: "tr",
            type: "error",
          });
        }
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = useCallback((field, value) => {
    setConfig((prev) => (prev ? { ...prev, [field]: value } : null));
    setValidationErrors([]);
  }, []);

  const handleEntityTypeToggle = useCallback((value, checked) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const current = prev.allowedEntityTypes || [];
      const next = checked
        ? [...current, value]
        : current.filter((v) => v !== value);
      return { ...prev, allowedEntityTypes: next };
    });
    setValidationErrors([]);
  }, []);

  const handleSave = useCallback(() => {
    if (!config) return;
    const errors = validateForm(config);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setSaving(true);
    setValidationErrors([]);
    sendAuthenticatedAsyncRequest(
      "/agent-workspace/ai-agent/ai-assigner-setup",
      "POST",
      config,
      (response) => {
        setConfig(response?.data != null ? { ...response.data } : config);
        setSaving(false);
        showNotification({
          title: "Success",
          message: "Config saved successfully.",
          position: "tc",
          type: "success",
        });
      },
      (err) => {
        setSaving(false);
        showNotification({
          title: "Error",
          message: getErrorMessage(err?.response?.data) || "Could not save config.",
          position: "tr",
          type: "error",
        });
      }
    );
  }, [config]);

  const handleCancel = useCallback(() => {
    setValidationErrors([]);
    fetchConfig();
  }, [fetchConfig]);

  const estimatedCalls = config
    ? getEstimatedPerDay(
        config.businessDayStartCst,
        config.businessDayEndCst,
        config.assignmentsPerRunCall
      )
    : null;
  const estimatedTasks = config
    ? getEstimatedPerDay(
        config.businessDayStartCst,
        config.businessDayEndCst,
        config.assignmentsPerRunTask
      )
    : null;

  const hasValidBatches =
    config &&
    Number.isInteger(Number(config.assignmentsPerRunCall)) &&
    Number(config.assignmentsPerRunCall) >= 1 &&
    Number.isInteger(Number(config.assignmentsPerRunTask)) &&
    Number(config.assignmentsPerRunTask) >= 1;

  const hasValidTimes =
    config &&
    config.businessDayStartCst &&
    config.businessDayEndCst &&
    HHMM_REGEX.test(config.businessDayStartCst) &&
    HHMM_REGEX.test(config.businessDayEndCst);

  const timeOrderOk =
    hasValidTimes && hasValidTimeOrder(config.businessDayStartCst, config.businessDayEndCst);

  const windowTooSmallForScheduler =
    hasValidBatches &&
    timeOrderOk &&
    isWindowTooSmallForScheduler(config.businessDayStartCst, config.businessDayEndCst);

  const showEndAfterStartHint = hasValidBatches && hasValidTimes && !timeOrderOk;

  if (loading && !config) {
    return (
      <div
        className="d-flex justify-content-center align-items-center py-5"
        data-testid="ai-agent-config-loading"
      >
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger" data-testid="ai-agent-config-load-error">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" data-testid="ai-agent-config-root">
      <div className="d-flex align-items-center mb-4 p-3 bg-primary text-white rounded">
        <span className="me-2">⚙</span>
        <h5 className="mb-0" data-testid="ai-agent-config-heading">
          AI Calls Configuration
        </h5>
      </div>

      <Row>
        <Col md={8}>
          <Card>
            <Card.Body>
              {validationErrors.length > 0 && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setValidationErrors([])}
                  className="p-3 mb-4"
                  data-testid="ai-agent-config-validation-alert"
                >
                  <ul className="mb-0 ps-3">
                    {validationErrors.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <h6 className="text-primary mb-3" data-testid="ai-agent-config-section-activity-settings">
                Activity Settings
              </h6>
              <Form.Group className="mb-3">
                <Form.Label>Call activity assignments</Form.Label>
                <Form.Control
                  type="number"
                  min={ASSIGNMENTS_PER_RUN_MIN}
                  max={config?.maxBatchSize ?? undefined}
                  step={1}
                  value={config?.assignmentsPerRunCall ?? ""}
                  onChange={(e) =>
                    handleChange(
                      "assignmentsPerRunCall",
                      e.target.value === "" ? null : parseInt(e.target.value, 10)
                    )
                  }
                  placeholder="# per 5 mins"
                  data-testid="ai-agent-config-input-assignments-call"
                />
                <Form.Text className="text-muted">
                  {config?.maxBatchSize != null
                    ? `Max call assignments per 5 minutes. Between 1 and ${config.maxBatchSize}.`
                    : "Max call assignments per 5 minutes. At least 1."}
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Task activity assignments</Form.Label>
                <Form.Control
                  type="number"
                  min={ASSIGNMENTS_PER_RUN_MIN}
                  max={config?.maxBatchSize ?? undefined}
                  step={1}
                  value={config?.assignmentsPerRunTask ?? ""}
                  onChange={(e) =>
                    handleChange(
                      "assignmentsPerRunTask",
                      e.target.value === "" ? null : parseInt(e.target.value, 10)
                    )
                  }
                  placeholder="# per 5 mins"
                  data-testid="ai-agent-config-input-assignments-task"
                />
                <Form.Text className="text-muted">
                  {config?.maxBatchSize != null
                    ? `Max task assignments per 5 minutes. Between 1 and ${config.maxBatchSize}.`
                    : "Max task assignments per 5 minutes. At least 1."}
                </Form.Text>
              </Form.Group>
              <p
                className="small fst-italic text-muted"
                data-testid="ai-agent-config-text-priority-hint"
              >
                *Activities sent based upon prioritization within 1 business day.
              </p>

              <hr className="my-4" />
              <h6 className="text-primary mb-3" data-testid="ai-agent-config-section-settings">
                Settings
              </h6>

              <Form.Group className="mb-3">
                <Form.Label data-testid="ai-agent-config-label-call-hours">Call Hours</Form.Label>
                <Row>
                  <Col md={6}>
                    <Form.Control
                      type="time"
                      min={BUSINESS_DAY_START_MIN}
                      max={BUSINESS_DAY_END_MAX}
                      value={config?.businessDayStartCst ?? ""}
                      onChange={(e) => handleChange("businessDayStartCst", e.target.value)}
                      data-testid="ai-agent-config-input-business-day-start"
                    />
                    <Form.Text className="text-muted">Start (e.g. 9:00 AM)</Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="time"
                      min={BUSINESS_DAY_START_MIN}
                      max={BUSINESS_DAY_END_MAX}
                      value={config?.businessDayEndCst ?? ""}
                      onChange={(e) => handleChange("businessDayEndCst", e.target.value)}
                      data-testid="ai-agent-config-input-business-day-end"
                    />
                    <Form.Text className="text-muted">End (e.g. 5:00 PM)</Form.Text>
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label data-testid="ai-agent-config-label-entity-type">Entity Type</Form.Label>
                <div>
                  {ENTITY_TYPE_OPTIONS.map(({ value, label }) => (
                    <Form.Check
                      key={value}
                      type="checkbox"
                      id={`entity-${value}`}
                      label={label}
                      checked={(config?.allowedEntityTypes || []).includes(value)}
                      onChange={(e) => handleEntityTypeToggle(value, e.target.checked)}
                      disabled
                      data-testid={`ai-agent-config-checkbox-entity-${value.toLowerCase()}`}
                    />
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="enabled"
                    label="Assigner enabled"
                    checked={config?.enabled ?? false}
                    onChange={(e) => handleChange("enabled", e.target.checked)}
                    data-testid="ai-agent-config-switch-assigner-enabled"
                  />
                </div>
              </Form.Group>

              <div className="d-flex gap-2 mt-4">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="ai-agent-config-btn-save"
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={saving}
                  data-testid="ai-agent-config-btn-cancel"
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3" data-testid="ai-agent-config-panel-estimated-volume">
            <Card.Header>
              <Card.Title as="h6">Estimated volume</Card.Title>
            </Card.Header>
            <Card.Body>
              {estimatedCalls != null || estimatedTasks != null ? (
                <>
                  {estimatedCalls != null && (
                    <p className="mb-2" data-testid="ai-agent-config-text-estimated-calls">
                      Up to <strong>{estimatedCalls}</strong> calls per day
                    </p>
                  )}
                  {estimatedTasks != null && (
                    <p className="mb-0" data-testid="ai-agent-config-text-estimated-tasks">
                      Up to <strong>{estimatedTasks}</strong> tasks per day
                    </p>
                  )}
                  <small className="text-muted">
                    Based on current call hours and batch sizes.
                  </small>
                </>
              ) : windowTooSmallForScheduler ? (
                <p
                  className="mb-0 text-danger"
                  data-testid="ai-agent-config-text-estimates-window-too-small"
                >
                  The time between start and end is less than {SCHEDULER_INTERVAL_MINUTES} minutes, so
                  the scheduler cannot run a full interval. Widen the call hours to see volume
                  estimates.
                </p>
              ) : showEndAfterStartHint ? (
                <p
                  className="mb-0 text-danger"
                  data-testid="ai-agent-config-text-estimates-end-before-start"
                >
                  End time must be after start time to see estimates.
                </p>
              ) : (
                <p className="mb-0 text-muted" data-testid="ai-agent-config-text-estimates-empty-hint">
                  Set call hours and batch sizes to see estimates.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AiAgentConfig;
